import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import api, { clearNativeAuthTokens, storeNativeAuthTokens } from "@/lib/api";
import { initializePushNotifications, unregisterPushNotifications } from "@/lib/pushNotifications";
import { isReadOnlyReviewer } from "@/lib/roles";

const AuthContext = createContext(null);

function isUserPayload(data) {
  return data && typeof data === "object" && !Array.isArray(data) && data.id && data.email;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const authGeneration = useRef(0);

  const checkAuth = useCallback(async () => {
    const generation = authGeneration.current;
    try {
      const { data } = await api.get("/auth/me");
      if (generation === authGeneration.current) {
        setUser(isUserPayload(data) ? data : false);
      }
    } catch {
      if (generation === authGeneration.current) {
        setUser(false);
      }
    } finally {
      if (generation === authGeneration.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (isUserPayload(user) && !isReadOnlyReviewer(user)) {
      initializePushNotifications(user);
    }
  }, [user]);

  const login = useCallback(async (email, password) => {
    authGeneration.current += 1;
    const { data } = await api.post("/auth/login", { email, password });
    if (!isUserPayload(data)) {
      throw new Error("Invalid authentication response");
    }
    await storeNativeAuthTokens(data);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    authGeneration.current += 1;
    await unregisterPushNotifications().catch(() => {});
    try {
      await api.post("/auth/logout");
    } catch {
      // Logout is best-effort on the server; we always clear local state
      // regardless so the user lands on the login page.
    }
    try {
      await clearNativeAuthTokens();
    } finally {
      setUser(false);
    }
  }, []);

  // Memoize the context value so consumers don't re-render whenever the
  // provider itself re-renders for unrelated reasons.
  const value = useMemo(
    () => ({ user, loading, login, logout, checkAuth }),
    [user, loading, login, logout, checkAuth],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
